/**
 * QuizBox - "Should I Buy This?" quiz component
 * Full-screen overlay with theme-aware gradients, emoji options, and purchase score results.
 */
import { useState } from "react";
import { ChevronRight, Lock, X, Check } from "lucide-react";
import { useSubscription } from "../../contexts/SubscriptionContext";

export const questions = [
  {
    id: 1,
    question: "Do you need this item, or do you just want it?",
    options: [
      { emoji: "✅", label: "I absolutely need it", subtitle: "It serves an essential purpose", tag: "Need", tagType: "good", score: 3 },
      { emoji: "🤷🏾‍♀️", label: "I want it but could live without it", subtitle: "Nice to have, not essential", score: 2 },
      { emoji: "💭", label: "It's purely a want", subtitle: "No real need, just desire", score: 1 },
    ]
  },
  {
    id: 2,
    question: "How much have you researched this purchase?",
    options: [
      { emoji: "🔍", label: "I've done thorough research", subtitle: "Compared prices and read reviews", tag: "Smart", tagType: "good", score: 3 },
      { emoji: "📱", label: "I've looked into it a little", subtitle: "Some research, not extensive", score: 2 },
      { emoji: "🎯", label: "No research at all", subtitle: "Going with my gut feeling", tag: "Risky", tagType: "bad", score: 1 },
    ]
  },
  {
    id: 3,
    question: "Do you already own something similar?",
    options: [
      { emoji: "🆕", label: "No, this is completely new", subtitle: "Nothing like it in my collection", tag: "Great", tagType: "good", score: 3 },
      { emoji: "🤔", label: "Maybe something similar", subtitle: "I have something that partly works", score: 2 },
      { emoji: "📦", label: "Yes, I have similar items", subtitle: "Already own something like this", score: 1 },
    ]
  },
  {
    id: 4,
    question: "Have you waited at least 24 hours before deciding?",
    options: [
      { emoji: "⏰", label: "Yes, I've waited and still want it", subtitle: "The desire hasn't faded", score: 3 },
      { emoji: "😐", label: "I've thought about it for a while", subtitle: "Not quite 24 hours but considered", score: 2 },
      { emoji: "🙃", label: "No, I want to buy it right now", subtitle: "Impulse purchase territory", tag: "Risky", tagType: "bad", score: 1 },
    ]
  },
  {
    id: 5,
    question: "Will this item still make you happy in 30 days?",
    options: [
      { emoji: "😊", label: "Definitely, it has lasting value", subtitle: "Long-term satisfaction guaranteed", tag: "Great", tagType: "good", score: 3 },
      { emoji: "🤷🏾‍♀️", label: "Probably, but I'm not sure", subtitle: "The excitement might fade", score: 2 },
      { emoji: "😅", label: "Honestly, probably not", subtitle: "Short-lived excitement", score: 1 },
    ]
  },
  {
    id: 6,
    question: "Can you afford this without touching your savings?",
    options: [
      { emoji: "💰", label: "Yes, it fits within my budget", subtitle: "Within leftover budget this month", tag: "Great", tagType: "good", score: 3 },
      { emoji: "😬", label: "I'd need to dip into savings", subtitle: "Would affect my savings goals", score: 2 },
      { emoji: "🤌", label: "I'd need to use credit", subtitle: "Can't pay cash for this", tag: "Risky", tagType: "bad", score: 1 },
    ]
  },
  {
    id: 7,
    question: "What would future you think about this purchase?",
    options: [
      { emoji: "🥳", label: "They'd be grateful", subtitle: "A purchase I'll be glad I made", score: 3 },
      { emoji: "😶", label: "They'd be neutral about it", subtitle: "Neither regret nor celebrate", score: 2 },
      { emoji: "😬", label: "They'd probably regret it", subtitle: "Future me would be disappointed", score: 1 },
    ]
  }
];

// Theme configs
const themeConfig = {
  pink: {
    headerGradient: '#FFFFFF',
    bodyBg: '#FBEFF5',
    radioBg: 'bg-pink-500',
    radioBorder: 'border-pink-300',
    selectedBorder: 'border-pink-300',
    selectedBg: 'bg-pink-50',
    nextGradient: 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)',
    scoreTextColor: 'text-pink-500',
    progressFill: 'bg-gradient-theme',
    progressEmpty: 'bg-[#F0E5EC]',
    questionCounter: 'text-pink-200',
    dotColor: 'bg-pink-300',
    radioGradient: 'linear-gradient(135deg, #F5A8C1, #E27BA1)',
  },

  blue: {
    headerGradient: 'linear-gradient(135deg, #E0E7FF, #DBEAFE)',
    bodyBg: '#FFFFFF',
    radioBg: 'bg-indigo-500',
    radioBorder: 'border-indigo-300',
    selectedBorder: 'border-indigo-300',
    selectedBg: 'bg-indigo-50',
    nextGradient: '#4361EE',
    scoreTextColor: 'text-indigo-500',
    progressFill: 'bg-indigo-300',
    progressEmpty: 'bg-indigo-200/40',
    questionCounter: 'text-indigo-200',
    dotColor: 'bg-indigo-300',
    radioGradient: 'linear-gradient(135deg, #6366f1, #4361EE)',
  },

  warm: {
    headerGradient: 'linear-gradient(135deg, #EDE3D8, #F0E5D2)',
    bodyBg: '#F8F3EB',
    radioBg: 'bg-[#7C5C3A]',
    radioBorder: 'border-[#c4a87a]',
    selectedBorder: 'border-[#c4a87a]',
    selectedBg: 'bg-[#faf3ea]',
    nextGradient: '#2D5A3D',
    scoreTextColor: 'text-[#7C5C3A]',
    progressFill: 'bg-[#b8956a]',
    progressEmpty: 'bg-[#d4c4a8]/50',
    questionCounter: 'text-[#d4c4a8]',
    dotColor: 'bg-[#b8956a]',
    radioGradient: 'linear-gradient(135deg, #B08D5B, #7C5C3A)',
  }
};

export const getResult = (answers) => {
  const totalScore = Object.values(answers).reduce((sum, idx) => {
    return sum + idx.score;
  }, 0);
  const maxScore = questions.length * 3;
  const percentage = Math.round((totalScore / maxScore) * 100);

  if (percentage >= 65) return {
    verdict: "Go For It!",
    emoji: "✅",
    emojiBg: "bg-emerald-200",
    scoreColor: "text-emerald-500",
    barColor: "bg-emerald-500",
    message: "This seems like a thoughtful purchase. You've considered it carefully and it fits your budget. Enjoy it!",
    primaryLabel: "🛍️ Add to Wishlist First",
    score: percentage,
  };
  if (percentage >= 40) return {
    verdict: "Sleep On It",
    emoji: "😴",
    emojiBg: "bg-amber-200",
    scoreColor: "text-amber-500",
    barColor: "bg-amber-500",
    message: "You're on the fence. Wait another day or two — if you still want it then, it might be worth it.",
    primaryLabel: "📁 Add to Wishlist & Wait",
    score: percentage,
  };
  return {
    verdict: "Skip It!",
    emoji: "🚫",
    emojiBg: "bg-red-200",
    scoreColor: "text-red-500",
    barColor: "bg-red-500",
    message: "Your future self will thank you. This purchase doesn't align with your goals right now. Put that money toward your savings instead!",
    primaryLabel: "💰 Save the Money Instead",
    score: percentage,
  };
};

const QuizBox = ({ onNavigateToTab, variant = 'card' }) => {
  const { hasAccess, showFeaturePaywall, theme } = useSubscription();
  const [isOpen, setIsOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  const isPremium = hasAccess('quiz');
  const t = themeConfig[theme] || themeConfig.pink;

  const getTriggerStyle = () => ({
    background: '#ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  });

  const handleAnswer = (questionId, option) => {
    setAnswers(prev => ({ ...prev, [questionId]: option }));
  };

  const resetQuiz = () => { setCurrentQuestion(0); setAnswers({}); setShowResult(false); };
  const closeAndReset = () => { setIsOpen(false); resetQuiz(); };
  const allAnswered = Object.keys(answers).length === questions.length;
  const q = questions[currentQuestion];
  const selectedOption = answers[q.id];

  const handleTriggerClick = () => {
    if (isPremium) setIsOpen(true);
    else showFeaturePaywall('quiz');
  };

  return (
    <>
      {variant === 'row' ? (
        // Home lists the buying tools as rows inside one shared card.
        <button
          onClick={handleTriggerClick}
          className="w-full bg-white px-4 py-3.5 text-left"
          data-testid="quiz-trigger-box"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
              style={{ background: theme === 'warm' ? '#F0E6D8' : theme === 'blue' ? '#EEF1FE' : '#F3ECF7' }}
            >
              {isPremium ? '🤔' : <Lock className="w-4 h-4 text-gray-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="m-0 text-[15px] truncate" style={{ color: '#1F2937', fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
                Should I Buy This?
              </p>
              <p className="m-0 mt-0.5 text-[12px] text-gray-500 truncate">
                {isPremium ? 'Take the quiz before purchasing' : 'Upgrade to unlock'}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
          </div>
        </button>
      ) : variant === 'tile' ? (
        // Wishlist shows the buying tools as two squares at the top.
        <button
          onClick={handleTriggerClick}
          className="w-full h-full rounded-2xl p-3.5 text-left flex flex-col"
          style={{ background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', minHeight: '118px' }}
          data-testid="quiz-trigger-tile"
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0" style={{ background: (theme === 'warm' ? '#F0E6D8' : theme === 'blue' ? '#EEF1FE' : '#F3ECF7') }}>
            🤔
          </div>
          <p className="m-0 mt-3 text-[15px]" style={{ color: '#1F2937', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.2 }}>Should I<br />Buy This?</p>
          <p className="m-0 mt-1 text-[12px] text-gray-500 leading-snug">Take the quiz</p>
        </button>
      ) : (
      <button
        onClick={handleTriggerClick}
        className="w-full rounded-2xl p-3 transition-all text-left group relative overflow-hidden"
        style={getTriggerStyle()}
        data-testid="quiz-trigger-box"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            {isPremium ? <span className="text-2xl">🤔</span> : <Lock className="w-5 h-5 text-gray-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg text-gray-900" style={{ fontFamily: "'DM Serif Display', serif", letterSpacing: '-0.3px' }}>Should I Buy This?</h3>
            <p className="text-xs text-gray-500 truncate">{isPremium ? 'Take the quiz before purchasing' : 'Upgrade to unlock'}</p>
          </div>
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: isPremium ? (theme === 'warm' ? 'rgba(124,92,58,0.10)' : theme === 'blue' ? 'rgba(99,102,241,0.10)' : 'rgba(236,72,153,0.10)') : 'rgba(156,163,175,0.10)' }}>
            <ChevronRight className="w-4 h-4" style={{ color: isPremium ? (theme === 'warm' ? '#7C5C3A' : theme === 'blue' ? '#6366f1' : '#EC4899') : '#9CA3AF' }} />
          </div>
        </div>
      </button>
      )}

      {/* Full-screen Quiz Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex flex-col wy-plan-tool" data-patch="plan_10" style={{ background: t.bodyBg, paddingTop: "max(env(safe-area-inset-top), 12px)" }}>
          {/* Gradient Header */}
          <div className="relative mx-4 mt-2 px-5 pt-4 pb-5 rounded-[28px] overflow-hidden" style={{ background: t.headerGradient }}>
            {/* Title row */}
            <div className="flex items-center justify-between mb-4 relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center">
                  <span className="text-3xl">🤔</span>
                </div>
                <h2 className="text-gray-800 text-xl font-bold" style={{ fontFamily: "'DM Serif Display', serif" }}>
                  Should I Buy This?
                </h2>
              </div>
              <button
                data-testid="quiz-close-btn"
                onClick={closeAndReset}
                className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Progress pills */}
            <div className="flex items-center gap-1.5 mb-1 relative z-10">
              {questions.map((_, i) => {
                const isCurrent = showResult ? false : i === currentQuestion;
                const isCompleted = showResult || i < currentQuestion || answers[questions[i].id] !== undefined;
                return (
                  <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      isCurrent ? `${t.progressFill} flex-[2]` :
                      isCompleted ? `${t.progressFill} flex-[1.5]` :
                      `${t.progressEmpty} flex-1`
                    }`}
                  />
                );
              })}
            </div>

            {/* Question counter / Complete label */}
            <p className="text-xs font-medium relative z-10" style={{ color: '#6B6B73' }}>
              {showResult ? 'Complete!' : `Question ${currentQuestion + 1} of ${questions.length}`}
            </p>

            {/* Question text or results header */}
            {showResult ? (
              <p className="text-gray-800 text-xl font-bold mt-2 relative z-10" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Your results are in ✨
              </p>
            ) : (
              <h3 className="text-xl font-bold mt-3 relative z-10 leading-tight text-gray-800" style={{ fontFamily: "'DM Serif Display', serif" }}>
                {q.question}
              </h3>
            )}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 pt-5 pb-32">
            {!showResult ? (
              /* Question Options */
              <div className="space-y-3">
                {q.options.map((option, idx) => {
                  const isSelected = selectedOption?.label === option.label;
                  return (
                    <button
                      key={idx}
                      data-testid={`quiz-option-${idx}`}
                      onClick={() => handleAnswer(q.id, option)}
                      className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 transition-all text-left ${
                        isSelected
                          ? `${t.selectedBorder} ${t.selectedBg}`
                          : 'border-transparent bg-white'
                      }`}
                    >
                      {/* Radio circle */}
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected ? '' : 'border-2 border-gray-300'
                      }`} style={isSelected ? { background: t.radioGradient } : undefined}>
                        {isSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                      </div>

                      {/* Emoji */}
                      <span className="text-2xl flex-shrink-0">{option.emoji}</span>

                      {/* Text */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 text-sm leading-tight">{option.label}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">{option.subtitle}</p>
                      </div>

                      {/* Tag badge */}
                      {isSelected && option.tag && (
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${
                          option.tagType === 'good'
                            ? 'bg-emerald-100 text-emerald-600'
                            : 'bg-red-100 text-red-500'
                        }`}>
                          {option.tag}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              /* Results */
              (() => {
                const result = getResult(answers);
                return (
                  <div className="text-center pt-2">
                    {/* Large emoji icon */}
                    <div className={`w-24 h-24 rounded-3xl ${result.emojiBg} flex items-center justify-center mx-auto mb-5`}>
                      <span className="text-5xl">{result.emoji}</span>
                    </div>

                    {/* Verdict */}
                    <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
                      {result.verdict}
                    </h2>
                    <p className="text-gray-500 text-sm leading-relaxed mb-6 px-2">{result.message}</p>

                    {/* Score card */}
                    <div className="rounded-2xl border border-gray-200 p-4 mb-2" style={{ background: t.bodyBg }}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-600">Purchase Score</span>
                        <span className={`text-2xl font-bold ${result.scoreColor}`} style={{ fontFamily: "'DM Serif Display', serif" }}>
                          {result.score} <span className="text-gray-300">/ 100</span>
                        </span>
                      </div>
                      {/* Score bar */}
                      <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${result.barColor}`}
                          style={{ width: `${result.score}%` }}
                        />
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="mt-6 space-y-3">
                      <button
                        data-testid="quiz-result-primary-btn"
                        onClick={() => {
                          const r = getResult(answers);
                          closeAndReset();
                          if (r.verdict === "Skip It!") {
                            onNavigateToTab?.('budget');
                          } else {
                            onNavigateToTab?.('wishlist');
                          }
                        }}
                        className="w-full py-4 rounded-2xl text-white font-bold text-sm"
                        style={{ background: t.nextGradient }}
                      >
                        {result.primaryLabel}
                      </button>
                      <button
                        data-testid="quiz-result-retry-btn"
                        onClick={resetQuiz}
                        className="w-full py-4 rounded-2xl bg-gray-100 text-gray-700 font-bold text-sm"
                      >
                        🔁 Try Another Item
                      </button>
                    </div>
                  </div>
                );
              })()
            )}
          </div>

          {/* Fixed bottom navigation (questions only) */}
          {!showResult && (
            <div className="fixed bottom-0 left-0 right-0 px-5 flex items-center justify-between" style={{ background: t.bodyBg, paddingTop: "16px", paddingBottom: "calc(env(safe-area-inset-bottom) + 80px)" }}>
              <button
                onClick={() => setCurrentQuestion(p => Math.max(0, p - 1))}
                disabled={currentQuestion === 0}
                className="py-3 text-gray-500 font-semibold text-sm disabled:opacity-0 transition-opacity"
              >
                ← Previous
              </button>
              {currentQuestion < questions.length - 1 ? (
                <button
                  data-testid="quiz-next-btn"
                  onClick={() => setCurrentQuestion(p => p + 1)}
                  disabled={!selectedOption}
                  className="px-8 py-3 rounded-2xl text-white font-bold text-sm disabled:opacity-40 transition-opacity"
                  style={{ background: t.nextGradient }}
                >
                  Next →
                </button>
              ) : (
                <button
                  data-testid="quiz-see-result-btn"
                  onClick={() => setShowResult(true)}
                  disabled={!allAnswered}
                  className="px-8 py-3 rounded-2xl text-white font-bold text-sm disabled:opacity-40 transition-opacity"
                  style={{ background: t.nextGradient }}
                >
                  See Result →
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default QuizBox;
